import type { Storefront } from '@/hooks/useStorefront';
import { currencyForStorefront } from '@/lib/storefrontCurrency';

/**
 * Amounts as they will be BOTH displayed and charged. Also carries the CHF
 * base snapshot that is persisted on the order row (see P2.4 migration).
 *
 * CH fastpath: `*_base` are byte-identical to the input display amounts.
 * No conversion, no rounding, no re-computation — pure pass-through.
 */
export interface OrderAmounts {
  // Display / charge amounts (in storefront currency)
  subtotal: number;
  discount: number;
  vat_amount: number;
  total: number;
  payment_fee: number | null;
  // Snapshot metadata
  currency: string;
  base_currency: 'CHF';
  exchange_rate: number;
  // CHF snapshot (always in base currency)
  subtotal_base: number;
  discount_base: number;
  vat_amount_base: number;
  total_base: number;
  payment_fee_base: number | null;
}

export interface BuildOrderAmountsInput {
  storefront: Storefront;
  subtotal: number;
  discount: number;
  vat_amount: number;
  total: number;
  payment_fee?: number | null;
  /** Frozen session exchange_rate. CH is always 1. */
  exchange_rate?: number;
}

/**
 * Single source of truth for order amounts. Called from both CheckoutPage
 * insert sites so display, charge and snapshot are guaranteed to match.
 *
 * CH fastpath: values are passed through UNCHANGED; `*_base` mirrors the
 * display amounts; exchange_rate=1; currency='CHF'. This makes CH order
 * inserts bit-identical to the pre-P2.4b payload except for the additive
 * snapshot columns.
 */
export function buildOrderAmounts(input: BuildOrderAmountsInput): OrderAmounts {
  const {
    storefront,
    subtotal,
    discount,
    vat_amount,
    total,
    payment_fee = null,
  } = input;

  if (storefront === 'ch') {
    return {
      subtotal,
      discount,
      vat_amount,
      total,
      payment_fee,
      currency: 'CHF',
      base_currency: 'CHF',
      exchange_rate: 1,
      subtotal_base: subtotal,
      discount_base: discount,
      vat_amount_base: vat_amount,
      total_base: total,
      payment_fee_base: payment_fee,
    };
  }

  // Non-CH: not live yet (LIVE_STOREFRONTS=['ch']). Compute base via frozen
  // session rate. Overrides already fed into subtotal/total upstream in
  // useDisplayPriceSum, so the display-currency values are authoritative.
  const rate = input.exchange_rate && input.exchange_rate > 0 ? input.exchange_rate : 1;
  const toBase = (v: number) => v / rate;
  return {
    subtotal,
    discount,
    vat_amount,
    total,
    payment_fee,
    currency: currencyForStorefront(storefront),
    base_currency: 'CHF',
    exchange_rate: rate,
    subtotal_base: toBase(subtotal),
    discount_base: toBase(discount),
    vat_amount_base: toBase(vat_amount),
    total_base: toBase(total),
    payment_fee_base: payment_fee == null ? null : toBase(payment_fee),
  };
}
